import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { pluck } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, ComponentLoader } from 'components';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import fetch from 'features/common/fetch';
import campaignState from 'features/views/campaignSettings/states';

import DragContainer from './components/DragContainer';

export const CampaignState = ({ campaignId, contactStatesCodelist }) => {
	const dispatch = useDispatch();

	const states = useSelector((state) => campaignState.selectors.getStates(state, contactStatesCodelist));
	const usedStateIds = useSelector(campaignState.selectors.getUsedStateIds);
	const enableSave = useSelector(campaignState.selectors.getEnableSave);

	useEffect(() => {
		dispatch(campaignState.actions.fetchCampaignState(campaignId));
		// eslint-disable-next-line
	}, [dispatch]);

	const handleLoadCampaignState = useCallback(() => {
		dispatch(campaignState.actions.fetchCampaignState(campaignId));
	}, [dispatch, campaignId]);

	const handleReorder = useCallback(
		(states) => {
			dispatch(campaignState.actions.updateCampaignStates(states.map((state) => Number(state.id))));
		},
		[dispatch],
	);

	const handleRemove = useCallback(
		(statusId) => {
			if (usedStateIds.includes(+statusId)) {
				NiceModal.show(MODALS.lightbox_modal, {
					title: getLabelMessage('campaignState:usedStateWarning'),
					closeButtonLabel: getLabelMessage('common:ok'),
					showConfirmButton: false,
				});
			} else {
				dispatch(campaignState.actions.removeCampaignState(+statusId));
			}
		},
		[dispatch, usedStateIds],
	);

	const handleOnSave = useCallback(() => {
		dispatch(campaignState.actions.saveCampaignState(campaignId, pluck('id', states)));
	}, [dispatch, campaignId, states]);

	return (
		<Paper sx={{ p: 3, borderRadius: 2 }}>
			<SectionHeader sx={{ mb: 2 }} text={getLabelMessage('campaignState:tableHeader')} />
			<Grid container>
				<Grid size={12}>
					<Grid container spacing={5}>
						<Grid size={12}>
							<ComponentLoader
								fetchStorePath={fetch.constants.urls.campaignStatus}
								onReload={handleLoadCampaignState}
							>
								<DragContainer items={states} onRemove={handleRemove} onReorder={handleReorder} />
							</ComponentLoader>
						</Grid>
						<Grid size={4} />
						<Grid size={4}>
							<Button fullWidth variant="contained" onClick={handleOnSave} disabled={!enableSave}>
								{getLabelMessage('common:save')}
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
};

CampaignState.propTypes = {
	campaignId: PropTypes.number,
	contactStatesCodelist: PropTypes.arrayOf(PropTypes.object),
};

export default codelistProvider(codelists.solutionState, 'contactStatesCodelist')(CampaignState);
